import React, { useEffect, useMemo, useRef, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Textarea } from '@/shared/components/ui/textarea'
import { Calendar } from '@/shared/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover'
import { BaseLayoutContext, BaseLayoutSection } from '../../types/base-layout'
import { MissionStory } from '../../types/base-content'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import { ChevronLeft, ChevronRight, CalendarIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

interface MissionStoriesSectionProps {
  section: BaseLayoutSection
  context: BaseLayoutContext
}

function cloneStories(stories: MissionStory[]): MissionStory[] {
  return stories.map((story) => ({ ...story }))
}

/**
 * Extracts the first N sentences from a text string
 * @param text - The text to extract sentences from
 * @param sentenceCount - Number of sentences to extract (default: 4)
 * @returns The first N sentences, or the full text if it has fewer sentences
 */
/** Parse date-only string (YYYY-MM-DD) as local date to avoid timezone shifting to previous day. */
function parseLocalDateString(value: string): Date | null {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  const [, y, m, d] = match
  const year = parseInt(y!, 10)
  const month = parseInt(m!, 10) - 1
  const day = parseInt(d!, 10)
  const date = new Date(year, month, day)
  return isNaN(date.getTime()) ? null : date
}

/** Format story date for display at top of story. */
function formatStoryDate(value: string | null | undefined): string | null {
  if (!value || !value.trim()) return null
  try {
    const d = value.includes('T') ? parseISO(value) : parseLocalDateString(value)
    if (!d) return null
    return format(d, 'MMMM d, yyyy')
  } catch {
    return null
  }
}

function getFirstSentences(text: string | null | undefined, sentenceCount: number = 4): string {
  if (!text || !text.trim()) return ''
  
  // Split by sentence-ending punctuation followed by space or end of string
  // This regex matches: . ! ? followed by space, newline, or end of string
  const sentences = text.match(/[^.!?]+[.!?]+(?:\s+|$)/g) || []
  
  if (sentences.length <= sentenceCount) {
    return text.trim()
  }
  
  // Take first N sentences and join them
  const firstSentences = sentences.slice(0, sentenceCount).join(' ').trim()
  return firstSentences
}

/** First N lines of text for collapsed story preview. */
function getFirstLines(text: string | null | undefined, lineCount: number = 10): string {
  if (!text || !text.trim()) return ''
  const lines = text.split(/\r?\n/)
  if (lines.length <= lineCount) return text.trim()
  return lines.slice(0, lineCount).join('\n').trimEnd()
}

export function MissionStoriesSection({ section, context }: MissionStoriesSectionProps) {
  const stories = useMemo(() => context.missionStories ?? [], [context.missionStories])
  const canEdit = Boolean(context.permissions?.canManageMissionStories)
  const regionId = context.meta?.regionId ?? null
  const currentUserId = context.meta?.currentUserId ?? null
  const useAccordion = section.options?.['useAccordion'] === true

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isEditing, setIsEditing] = useState(false)
  const [editStories, setEditStories] = useState<MissionStory[]>(cloneStories(stories))
  const [saving, setSaving] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [storyToDelete, setStoryToDelete] = useState<{ id: number; index: number } | null>(null)
  const [accordionOpen, setAccordionOpen] = useState<string | undefined>(undefined)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const refreshStories = context.actions?.refreshMissionStories

  useEffect(() => {
    if (stories.length === 0) {
      setCurrentIndex(0)
      setEditStories([])
      return
    }
    setEditStories(cloneStories(stories))
    setCurrentIndex(0)
  }, [stories])

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const textarea = textareaRef.current
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [currentIndex, isEditing, editStories])

  const displayStories = useMemo(() => (isEditing ? editStories : stories), [isEditing, editStories, stories])
  const currentStory = displayStories[currentIndex] ?? null

  const goToIndex = (index: number) => {
    if (displayStories.length === 0) return
    const normalized = ((index % displayStories.length) + displayStories.length) % displayStories.length
    setCurrentIndex(normalized)
  }

  const handleAddStory = () => {
    if (!regionId || !currentUserId) return
    const newStory: MissionStory = {
      id: 0,
      regionId,
      userId: currentUserId,
      description: '',
      storyDate: format(new Date(), 'yyyy-MM-dd'),
      uploadedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setEditStories((prev) => {
      const next = [...prev, newStory]
      setCurrentIndex(next.length - 1)
      return next
    })
  }

  const handleDeleteConfirm = async () => {
    if (!storyToDelete) return
    const { id, index } = storyToDelete

    try {
      if (id) {
        const response = await fetch(`/api/mission-stories/${id}`, { method: 'DELETE' })
        if (!response.ok) {
          errorToast('Failed to delete mission story')
          return
        }
        successToast('Mission story deleted')
        if (typeof refreshStories === 'function') {
          await refreshStories()
        }
      } else {
        setEditStories((prev) => prev.filter((_, idx) => idx !== index))
      }
    } catch (error) {
      console.error('Failed to delete mission story', error)
      errorToast(error instanceof Error ? error.message : 'Failed to delete mission story')
    } finally {
      setDeleteDialogOpen(false)
      setStoryToDelete(null)
    }
  }

  const handleSaveStories = async () => {
    if (!regionId || !currentUserId) return

    try {
      setSaving(true)
      const newStories = editStories.filter((story) => story.id === 0 && story.description?.trim())
      const updatedStories = editStories.filter((story) => story.id !== 0)

      for (const story of newStories) {
        await fetch('/api/mission-stories', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            regionId,
            userId: currentUserId,
            description: story.description,
            storyDate: story.storyDate && story.storyDate.trim() ? story.storyDate.trim() : undefined,
          }),
        })
      }

      for (const story of updatedStories) {
        await fetch(`/api/mission-stories/${story.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            description: story.description,
            userId: currentUserId,
            storyDate: story.storyDate && story.storyDate.trim() ? story.storyDate.trim() : undefined,
          }),
        })
      }

      successToast('Mission stories saved')
      if (typeof refreshStories === 'function') {
        await refreshStories()
      }
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save mission stories', error)
      errorToast(error instanceof Error ? error.message : 'Failed to save mission stories')
    } finally {
      setSaving(false)
    }
  }

  const handleCancelEditing = () => {
    setIsEditing(false)
    setEditStories(cloneStories(stories))
    setCurrentIndex(stories.length > 0 ? Math.min(currentIndex, stories.length - 1) : 0)
  }

  return (
    <Card key={section.id}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{section.title ?? 'Mission Stories'}</CardTitle>
        {canEdit && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="ghost" onClick={handleSaveStories} disabled={saving} showLoader={saving}>
                  Save
                </Button>
                <Button variant="outline" onClick={handleCancelEditing} disabled={saving} showLoader={false}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button 
                size="sm" 
                onClick={() => setIsEditing(true)}
                className="bg-primary hover:bg-primary/90 text-white font-bold"
              >
                Edit Stories
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {displayStories.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {canEdit ? 'No mission stories yet. Use Edit Stories to add one.' : 'No mission stories available.'}
          </p>
        ) : useAccordion && !isEditing ? (
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => goToIndex(currentIndex - 1)}
              disabled={displayStories.length <= 1}
              showLoader={false}
              className="h-8 w-8 flex-shrink-0"
              aria-label="Previous story"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <Accordion
              type="single"
              collapsible
              className="w-full flex-1 min-w-0"
              value={accordionOpen}
              onValueChange={setAccordionOpen}
            >
            {(() => {
              const currentStory = displayStories[currentIndex]
              if (!currentStory) return null
              
              const storyDateFormatted = formatStoryDate(currentStory.storyDate)
              const fullText = currentStory.description || 'No story text provided.'
              const previewText = getFirstLines(fullText, 10)
              const hasMore = fullText.trim() !== previewText.trim()
              
              return (
                <AccordionItem value="current-story">
                  <AccordionTrigger className="text-left">
                    {accordionOpen !== 'current-story' && (
                      <div className="flex-1 pr-4 space-y-1">
                        {storyDateFormatted && (
                          <p className="text-xs font-medium text-foreground">{storyDateFormatted}</p>
                        )}
                        <p className="whitespace-pre-wrap text-sm text-muted-foreground line-clamp-[10]">
                          {previewText}
                          {hasMore && '...'}
                        </p>
                      </div>
                    )}
                    {accordionOpen === 'current-story' && (
                      <div className="flex-1 pr-4">
                        <p className="text-sm font-medium text-foreground">Mission Story</p>
                      </div>
                    )}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-2">
                      {storyDateFormatted && (
                        <p className="text-sm font-medium text-foreground">{storyDateFormatted}</p>
                      )}
                      <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                        {fullText}
                      </p>
                      {displayStories.length > 1 && (
                        <div className="flex items-center justify-center gap-2">
                          {displayStories.map((_, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                goToIndex(idx)
                              }}
                              className={`h-2.5 w-6 rounded-full transition-colors ${
                                idx === currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                              }`}
                              aria-label={`Show story ${idx + 1}`}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )
            })()}
          </Accordion>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => goToIndex(currentIndex + 1)}
            disabled={displayStories.length <= 1}
            showLoader={false}
            className="h-8 w-8 flex-shrink-0"
            aria-label="Next story"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => goToIndex(currentIndex - 1)}
                disabled={displayStories.length <= 1}
                showLoader={false}
                className="h-8 w-8 flex-shrink-0"
                aria-label="Previous story"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex-1 space-y-3 rounded-md border border-border bg-muted/40 p-4">
                {isEditing ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(
                              'justify-start text-left font-normal',
                              !editStories[currentIndex]?.storyDate && 'text-muted-foreground',
                            )}
                            showLoader={false}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {editStories[currentIndex]?.storyDate
                              ? formatStoryDate(editStories[currentIndex].storyDate)
                              : 'Pick story date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={(() => {
                              const raw = editStories[currentIndex]?.storyDate
                              if (!raw) return undefined
                              const d = raw.includes('T') ? parseISO(raw) : parseLocalDateString(raw)
                              return d ?? undefined
                            })()}
                            onSelect={(date) => {
                              const value = date ? format(date, 'yyyy-MM-dd') : null
                              setEditStories((prev) =>
                                prev.map((story, idx) =>
                                  idx === currentIndex ? { ...story, storyDate: value ?? undefined } : story,
                                ),
                              )
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <Textarea
                      ref={textareaRef}
                      value={editStories[currentIndex]?.description ?? ''}
                      onChange={(event) => {
                        const value = event.target.value
                        setEditStories((prev) =>
                          prev.map((story, idx) => (idx === currentIndex ? { ...story, description: value } : story)),
                        )
                      }}
                      placeholder="Write the mission story..."
                      className="min-h-[160px] resize-none"
                    />
                  </>
                ) : (
                  <>
                    {currentStory?.storyDate && formatStoryDate(currentStory.storyDate) && (
                      <p className="text-sm font-medium text-foreground">
                        {formatStoryDate(currentStory.storyDate)}
                      </p>
                    )}
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                      {currentStory?.description || 'No story text provided.'}
                    </p>
                  </>
                )}

                {isEditing && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (displayStories.length === 0) return
                        setStoryToDelete({ id: editStories[currentIndex]?.id ?? 0, index: currentIndex })
                        setDeleteDialogOpen(true)
                      }}
                      showLoader={false}
                    >
                      Delete Story
                    </Button>
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => goToIndex(currentIndex + 1)}
                disabled={displayStories.length <= 1}
                showLoader={false}
                className="h-8 w-8 flex-shrink-0"
                aria-label="Next story"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center justify-center gap-2">
              {displayStories.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => goToIndex(idx)}
                  className={`h-2.5 w-6 rounded-full transition-colors ${idx === currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                  aria-label={`Show story ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        )}

        {isEditing && (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={handleAddStory} showLoader={false}>
              Add Story
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete mission story</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} showLoader={false}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} showLoader={false}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
